import { catchError, map, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { HandleError, HttpErrorHandler } from '@shared/services/error-handler/http-error-handler.service';
import { BusinessProfileService } from '@services/business-profile/business-profile.service';
import { BusinessProfile } from '@shared/meta-data/business-profile.meta';
import { AppConfig } from '@app/app.config';
import {
  IntegrationCatalogProvider,
  IntegrationRequest,
  IntegrationRequestPayload,
  IResponse,
  LinkedAccount,
  LinkedAccountEvent
} from '@shared/meta-data';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LinkedAccountsService {
  private readonly handleError: HandleError;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly httpErrorHandler: HttpErrorHandler,
    private readonly businessProfileService: BusinessProfileService
  ) {
    this.handleError = httpErrorHandler.createHandleError('Linked Accounts');
  }

  installShopify(subdomain: string): void {
    this.businessProfileService.getBusinessProfile()
      .pipe(take(1))
      .subscribe({
        next: (businessProfile: BusinessProfile) => {
          window.open(
            `${AppConfig.API_URL}/shopify/install?shop=${subdomain}.myshopify.com&user_id=${businessProfile.user_id}&business_profile_id=${businessProfile.business_profile_id}`, '_self'
          );
        }
      });
  }

  installStripe(): void {
    this.businessProfileService.getBusinessProfile()
      .pipe(take(1))
      .subscribe({
        next: (businessProfile: BusinessProfile) => {
          window.open(
            `${AppConfig.API_URL}/stripe/apps/install?user_id=${businessProfile.user_id}&business_profile_id=${businessProfile.business_profile_id}`, '_self'
          );
        }
      });
  }

  installAmazon(): void {
    this.businessProfileService.getBusinessProfile()
      .pipe(take(1))
      .subscribe({
        next: (businessProfile: BusinessProfile) => {
          window.open(
            `${AppConfig.API_URL}/amazon/install?user_id=${businessProfile.user_id}&business_profile_id=${businessProfile.business_profile_id}`, '_self'
          );
        }
      });
  }

  /**
   * Opens WooCommerce OAuth (wc-auth). Requires site URL only; API keys are issued after approval on the store.
   */
  installWooCommerce(siteUrl: string): Observable<BusinessProfile> {
    return this.businessProfileService.getBusinessProfile()
      .pipe(
        take(1),
        tap((businessProfile: BusinessProfile) => {
          window.open(
            `${AppConfig.API_URL}/woocommerce/install?site_url=${encodeURIComponent(siteUrl)}&user_id=${businessProfile.user_id}&business_profile_id=${businessProfile.business_profile_id}`,
            '_self'
          );
        })
      );
  }

  installMagento(consumerKey: string, consumerSecret: string, accessToken: string, accessTokenSecret: string): void {
    this.businessProfileService.getBusinessProfile()
      .pipe(take(1))
      .subscribe({
        next: (businessProfile: BusinessProfile) => {
          window.open(
            `${AppConfig.API_URL}/magento/install?consumer_key=${encodeURIComponent(consumerKey)}&consumer_secret=${encodeURIComponent(consumerSecret)}&access_token=${encodeURIComponent(accessToken)}&access_token_secret=${encodeURIComponent(accessTokenSecret)}&user_id=${businessProfile.user_id}&business_profile_id=${businessProfile.business_profile_id}`,
            '_self'
          );
        }
      });
  }

  fetchLinkedAccounts() {
    return this.httpClient.get<IResponse<LinkedAccount>>(`${AppConfig.API_URL}/linked-tokens`)
      .pipe(
        map(({ content }) => content),
        catchError((err) => {
          return this.handleError('Fetch Linked Account', null)(err);
        })
      );
  }

  fetchLinkedAccountsEvents() {
    return this.httpClient.get<IResponse<LinkedAccountEvent>>(`${AppConfig.API_URL}/linked-tokens/events`)
      .pipe(
        map(({ content }) => content),
        catchError((err) => {
          return this.handleError('Fetch Linked Account Events', null)(err);
        })
      );
  }

  unlinkAccount(tokenId: number): Observable<IResponse<{}>> {
    return this.httpClient.delete<IResponse<{}>>(`${AppConfig.API_URL}/linked-tokens/${tokenId}`)
      .pipe(catchError((err) => this.handleError('Remove Linked Account', null)(err)));
  }

  resyncShopify(tokenId: number): Observable<IResponse<{ webhooks_registered: boolean }>> {
    return this.httpClient.post<IResponse<{ webhooks_registered: boolean }>>(
      `${AppConfig.API_URL}/linked-tokens/${tokenId}/resync`,
      {}
    );
  }

  fetchIntegrationCatalog(): Observable<IntegrationCatalogProvider[]> {
    return this.httpClient
      .get<IResponse<IntegrationCatalogProvider[]>>(
        `${AppConfig.API_URL}/integration-credential-requests/catalog`
      )
      .pipe(map(({ content }) => content ?? []));
  }

  createIntegrationRequest(payload: IntegrationRequestPayload): Observable<IntegrationRequest> {
    return this.httpClient.post<IResponse<IntegrationRequest>>(
      `${AppConfig.API_URL}/integration-credential-requests`,
      payload
    )
      .pipe(map(({ content }) => content));
  }

  fetchIntegrationRequests(): Observable<IntegrationRequest[]> {
    return this.httpClient
      .get<IResponse<IntegrationRequest[]>>(`${AppConfig.API_URL}/integration-credential-requests`)
      .pipe(map(({ content }) => content ?? []));
  }

  cancelIntegrationRequest(requestId: string | number): Observable<void> {
    return this.httpClient.delete<void>(
      `${AppConfig.API_URL}/integration-credential-requests/${requestId}`
    );
  }
}